import React from 'react';
import { useFormContext, useWatch } from '@isyc-react/form/modules/rhf';
import { RhfInputText } from '@isyc-react/form/modules/input/text';
import { RhfInputDate } from '@isyc-react/form/modules/input/date';
import { RhfInputSelectIntl } from '@isyc-react/form/modules/input/select';
import { namespaces } from 'i18n/i18n.constants';
import { useTranslation } from 'react-i18next';
import { currentDate, dateFormatString, genericMinDate, numberFormatString, personSexOptions } from 'common-app/options';
import { getParameterName, RhfInputBailleursParameter, RhfInputBailleursParameterBooleanCheckbox, RhfInputBailleursParameterBooleanYesNo, RhfInputBailleursParameterSelectCheckbox, } from 'common-app/parameter';
import { getIsMinDateAllowed, useIsAdult } from 'common-app/types/bailleurs-utils';
import { validNameSurname } from 'common/utils';
export const OccupantForm = props => {
    const { name } = props;
    const { control, setValue } = useFormContext();
    const { t } = useTranslation(namespaces.common);
    const paramName = getParameterName(name);
    const watchBirthDate = useWatch({ name: `${name}.birthDate` });
    const watchAcharge = useWatch({ name: paramName('acharge') });
    const watchLienParente = useWatch({ name: paramName('lien_parente') });
    const watchGardeAlternee = useWatch({ name: paramName('garde_alternee') });
    const watchDroiteDeVisite = useWatch({ name: paramName('droite_de_visite') });
    React.useEffect(() => {
        if (watchAcharge.value === 'true') {
            setValue(`${name}.parameters.droite_de_visite`, Object.assign(Object.assign({}, watchDroiteDeVisite), { value: 'false' }));
        }
        else if (watchAcharge.value === 'false') {
            setValue(`${name}.parameters.garde_alternee`, Object.assign(Object.assign({}, watchGardeAlternee), { value: 'false' }));
        }
    }, [watchAcharge]);
    React.useEffect(() => {
        if (watchLienParente.value !== '1') {
            setValue(`${name}.parameters.droite_de_visite`, Object.assign(Object.assign({}, watchDroiteDeVisite), { value: 'false' }));
            setValue(`${name}.parameters.garde_alternee`, Object.assign(Object.assign({}, watchGardeAlternee), { value: 'false' }));
        }
    }, [watchLienParente]);
    const isAdult = useIsAdult(watchBirthDate, dateFormatString);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: 'row' },
            React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                React.createElement("label", { className: 'form-label' },
                    t('bailleurs.core.person.form.label.sex'),
                    " ",
                    React.createElement("span", { className: 'required' }, "*")),
                React.createElement(RhfInputSelectIntl, { rhf: { control, name: `${name}.sex`, rules: { required: true } }, input: {
                        namespace: namespaces.common,
                        isSearchable: false,
                        isClearable: true,
                        options: personSexOptions,
                    } }))),
        React.createElement("div", { className: 'row' },
            React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                React.createElement("label", { className: 'form-label' },
                    "Nom ",
                    React.createElement("span", { className: 'required' }, "*")),
                React.createElement(RhfInputText, { rhf: {
                        control,
                        name: `${name}.surname`,
                        rules: {
                            required: true,
                            validate: {
                                validChars: surname => {
                                    return validNameSurname(surname);
                                },
                            },
                        },
                    }, errorMessage: error => {
                        if (error.type === 'validChars')
                            return t('bailleurs.core.person.form.error.charsError');
                    } })),
            React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                React.createElement("label", { className: 'form-label' },
                    "Pr\u00E9nom ",
                    React.createElement("span", { className: 'required' }, "*")),
                React.createElement(RhfInputText, { rhf: {
                        control,
                        name: `${name}.firstname`,
                        rules: {
                            required: true,
                            validate: {
                                validChars: firstname => {
                                    return validNameSurname(firstname);
                                },
                            },
                        },
                    }, errorMessage: error => {
                        if (error.type === 'validChars')
                            return t('bailleurs.core.person.form.error.charsError');
                    } })),
            React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                React.createElement("label", { className: 'form-label' }, t('bailleurs.core.person.form.label.birthSurname')),
                React.createElement(RhfInputText, { rhf: {
                        control,
                        name: `${name}.birthName`,
                        rules: {
                            validate: {
                                validChars: birthName => {
                                    return validNameSurname(birthName);
                                },
                            },
                        },
                    }, errorMessage: error => {
                        if (error.type === 'validChars')
                            return t('bailleurs.core.person.form.error.charsError');
                    } })),
            React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                React.createElement("label", { className: 'form-label' },
                    t('bailleurs.core.person.form.label.birthDate'),
                    " ",
                    React.createElement("span", { className: 'required' }, "*")),
                React.createElement(RhfInputDate, { rhf: {
                        control,
                        name: `${name}.birthDate`,
                        rules: {
                            required: true,
                            validate: {
                                minYearAvailable: value => getIsMinDateAllowed(value),
                            },
                        },
                    }, input: {
                        placeholder: t('bailleurs.core.form.placeholder.date'),
                        dateFormat: dateFormatString,
                        numberFormat: numberFormatString,
                        minDate: genericMinDate,
                        maxDate: currentDate,
                    } }))),
        React.createElement("div", { className: 'row d-flex align-items-center' },
            React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-3' },
                React.createElement(RhfInputBailleursParameter, { rhf: { control, name: paramName('lien_parente'), rules: { required: true } }, remarks: React.createElement(React.Fragment, null, "D\u00E9crit le lien qui unit cet occupant \u00E0 un des titulaires du bail.") })),
            React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-3 mt-3 mt-lg-auto mb-2' },
                React.createElement(RhfInputBailleursParameterBooleanCheckbox, { rhf: { control, name: paramName('acharge') }, remarks: React.createElement(React.Fragment, null, "L'occupant est \u00E0 charge fiscalement (mentionn\u00E9 sur l'avis d'imp\u00F4t).") })),
            watchLienParente.value === '1' && (React.createElement(React.Fragment, null,
                watchAcharge.value === 'true' && (React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-3 mt-0 mt-lg-3 mt-xxl-auto mb-2' },
                    React.createElement(RhfInputBailleursParameterBooleanCheckbox, { rhf: { control, name: paramName('garde_alternee') }, remarks: React.createElement(React.Fragment, null, "L'enfant r\u00E9side \u00E0 temps \u00E9gal chez chacun de ses parents. ") }))),
                watchAcharge.value !== 'true' && (React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-3 mt-0 mt-lg-3 mt-xxl-auto mb-2' },
                    React.createElement(RhfInputBailleursParameterBooleanCheckbox, { rhf: { control, name: paramName('droite_de_visite') }, remarks: React.createElement(React.Fragment, null, "L'enfant r\u00E9side l'essentiel du temps chez l'autre parent. ") })))))),
        React.createElement("div", { className: 'row my-3' },
            React.createElement("div", { className: 'col-12 col-lg-auto d-flex align-items-center' },
                React.createElement(RhfInputBailleursParameterBooleanYesNo, { rhf: { control, name: paramName('toujours_dans_logement') } }))),
        React.createElement("div", { className: 'row my-2' },
            React.createElement("div", { className: 'd-flex align-items-center' },
                React.createElement(RhfInputBailleursParameterBooleanYesNo, { rhf: { control, name: paramName('handicap') }, input: { label: React.createElement(React.Fragment, null, "\u00CAtes-vous titulaire d'une carte d'invalidit\u00E9 ou d'une CMI mention invalidit\u00E9 ?") } }))),
        isAdult && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: 'row' },
                React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                    React.createElement(RhfInputBailleursParameter, { rhf: { control, name: paramName('code_csp'), rules: { required: true } } }))),
            React.createElement("div", { className: 'row my-4' },
                React.createElement("label", { className: 'form-label mb-3' },
                    "Nature des revenus ",
                    React.createElement("small", { className: 'fw-normal fst-italic' }, "(plusieurs choix possibles)"),
                    ":"),
                React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                    React.createElement(RhfInputBailleursParameterSelectCheckbox, { rhf: { control, name: paramName('Individu_Prestations') } })),
                React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                    React.createElement(RhfInputBailleursParameterSelectCheckbox, { rhf: { control, name: paramName('Individu_Ressources') } })))))));
};
// TODO update
export const OccupantSummary = props => {
    const { value } = props;
    return (React.createElement(React.Fragment, null,
        React.createElement("small", { className: 'd-flex align-items-center' },
            (value.firstname || value.surname) && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: 'me-2' },
                    value.firstname,
                    " ",
                    value.surname))),
            value.birthName && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: 'text-muted me-2' }, "|"),
                React.createElement("div", { className: 'me-2' }, value.birthName))),
            value.birthDate && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: 'text-muted me-2' }, "|"),
                React.createElement("div", { className: 'me-2' }, value.birthDate))))));
};
